import { OFFSET_LIMITS, SCALE_LIMITS, snapPosition } from './spreadLayoutUtils.js';
import { SHEET_GRID, snapEdges } from './sheetGrid.js';

/**
 * Placing a spread picture on its sheet's grid (sheetGrid). The math is in
 * sheet percent, x of the sheet's width and y of its height, on a frame that
 * says where the picture's page lies on the sheet:
 *
 *   page    { x, y, width, height }  the page on the sheet
 *   fit     { width, height }        the picture at scale 100: contained in the page, or covering it (fill)
 *   guides  { x: [], y: [] }         what it snaps to besides the grid (sheetGuides)
 *   aspect  the sheet's width over its height, to weigh one axis against the other
 *
 * A picture is saved as { scale, fill, x, y, ox, oy } (spreadLayoutUtils): x / y
 * place it in its window, from 0 (lead edges together) to 100 (trailing edges
 * together), and ox / oy move the window off the page, in percent of the page.
 * Without `free` a picture keeps to its page.
 */

const AXES = [
    { key: 'x', dim: 'width', offset: 'ox' },
    { key: 'y', dim: 'height', offset: 'oy' },
];

/** Less room than this, in sheet percent, and the position no longer moves the picture. */
const NO_ROOM = 0.01;

const round = (value) => Math.round(value * 100) / 100;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** One axis of a picture, at `scale`, on the sheet. */
function axisOf(frame, picture, { key, dim, offset }, scale = picture.scale || 100) {
    const start = frame.page[key];
    const page = frame.page[dim];
    const size = (frame.fit[dim] * scale) / 100;
    const room = page - size;
    const window = start + (page * (picture[offset] || 0)) / 100;
    return { start, page, size, room, position: picture[key], lead: window + (room * picture[key]) / 100 };
}

/** The lead edges a picture can have while it keeps to its page: inside it, or covering it. */
const onPage = (axis) => [axis.start + Math.min(0, axis.room), axis.start + Math.max(0, axis.room)];

/** The position (0–100) and offset (percent of the page) that put the picture's lead edge at `lead`. */
function place(axis, lead, offsetKey, free) {
    let { position } = axis;
    if (Math.abs(axis.room) >= NO_ROOM) position = round(clamp(((lead - axis.start) / axis.room) * 100, 0, 100));
    if (!free) return { position };
    const rest = lead - axis.start - (axis.room * position) / 100;
    const limit = OFFSET_LIMITS[offsetKey];
    return { position, offset: round(clamp((rest / axis.page) * 100, -limit, limit)) || 0 };
}

/** Saved fields for the picture at `scale` with its lead edges at `leads`; drops a line the page took it off. */
function placeAll(frame, picture, leads, { free, scale, lines }) {
    const out = {};
    AXES.forEach((spec) => {
        const axis = axisOf(frame, picture, spec, scale);
        let lead = leads[spec.key];
        if (!free) {
            const kept = clamp(lead, ...onPage(axis));
            if (kept !== lead) lines[spec.key] = null;
            lead = kept;
        }
        const placed = place(axis, lead, spec.offset, free);
        out[spec.key] = placed.position;
        if (free) out[spec.offset] = placed.offset;
    });
    return out;
}

/** A span of `size` from `lead`, moved so an edge or its middle is on a guide (the line) or the grid. */
function snapSpan(lead, size, guides) {
    const hit = snapEdges([lead, lead + size / 2, lead + size], guides);
    return { lead: lead + hit.shift, line: hit.guide ? hit.line : null };
}

/** A filled picture's axis: it pans inside its window, and past that (free) the window moves. */
function panAxis(axis, delta, guides, offsetKey, { free, snap }) {
    const lead = axis.lead + delta;
    const [lo, hi] = onPage(axis);
    const pans = Math.abs(axis.room) >= NO_ROOM;
    if (!free || (pans && lead >= lo && lead <= hi)) {
        let { position } = axis;
        if (pans) {
            const at = ((clamp(lead, lo, hi) - axis.start) / axis.room) * 100;
            position = snap ? snapPosition(at) : round(clamp(at, 0, 100));
        }
        return { position, offset: 0, line: null };
    }
    // Past its edge the window goes too, with the picture against its leading side.
    let position = axis.position;
    if (pans) position = lead > hi ? 0 : 100;
    const from = lead - (axis.room * position) / 100;
    const { lead: window, line } = snap ? snapSpan(from, axis.page, guides) : { lead: from, line: null };
    const limit = OFFSET_LIMITS[offsetKey];
    return { position, offset: round(clamp(((window - axis.start) / axis.page) * 100, -limit, limit)) || 0, line };
}

/**
 * A picture dragged by (dx, dy) sheet percent. Its lead or trailing edge or
 * its middle snaps to a guide within reach, or else to the nearest grid line.
 * A filled picture pans inside its window first, in POSITION_STEP steps; with
 * `free`, past that the window moves and snaps the same way.
 *
 * @returns {{ x: number, y: number, ox?: number, oy?: number, lines: { x: number|null, y: number|null } }}
 *   ox / oy only with `free`; lines are the guides it met, in sheet percent
 */
export function movePicture({ frame, picture, dx, dy, free = false, snap = true }) {
    const lines = { x: null, y: null };
    const delta = { x: dx, y: dy };
    if (!picture.fill) {
        const leads = {};
        AXES.forEach((spec) => {
            const axis = axisOf(frame, picture, spec);
            const moved = snap ? snapSpan(axis.lead + delta[spec.key], axis.size, frame.guides[spec.key]) : { lead: axis.lead + delta[spec.key], line: null };
            leads[spec.key] = moved.lead;
            lines[spec.key] = moved.line;
        });
        return { ...placeAll(frame, picture, leads, { free, scale: picture.scale || 100, lines }), lines };
    }
    const out = { lines };
    AXES.forEach((spec) => {
        const moved = panAxis(axisOf(frame, picture, spec), delta[spec.key], frame.guides[spec.key], spec.offset, { free, snap });
        out[spec.key] = moved.position;
        if (free) out[spec.offset] = moved.offset;
        lines[spec.key] = moved.line;
    });
    return out;
}

/**
 * A contained picture resized by dragging its `corner` (nw, ne, sw or se) by
 * (dx, dy) sheet percent. It keeps its shape and the opposite corner stays
 * put; the dragged corner's side or its top / bottom, whichever lands nearer,
 * meets a guide within reach or else the grid.
 *
 * @returns {{ scale: number, x: number, y: number, ox?: number, oy?: number, lines: { x: number|null, y: number|null } }}
 */
export function resizePicture({ frame, picture, corner, dx, dy, free = false, snap = true }) {
    const sign = { x: corner.includes('e') ? 1 : -1, y: corner.includes('s') ? 1 : -1 };
    const from = { x: axisOf(frame, picture, AXES[0]), y: axisOf(frame, picture, AXES[1]) };
    const fixed = {
        x: sign.x > 0 ? from.x.lead : from.x.lead + from.x.size,
        y: sign.y > 0 ? from.y.lead : from.y.lead + from.y.size,
    };
    const fit = { x: frame.fit.width, y: frame.fit.height };
    // The pointer's corner taken along the picture's diagonal, measured on the sheet's own shape.
    const aspect = frame.aspect || 1;
    const was = { x: from.x.size * aspect, y: from.y.size };
    const now = { x: (from.x.size + sign.x * dx) * aspect, y: from.y.size + sign.y * dy };
    let scale = (picture.scale || 100) * ((now.x * was.x + now.y * was.y) / (was.x * was.x + was.y * was.y));
    const lines = { x: null, y: null };
    if (snap) {
        const [best] = ['x', 'y'].map((key) => {
            const hit = snapEdges([fixed[key] + (sign[key] * fit[key] * scale) / 100], frame.guides[key]);
            const span = (hit.line - fixed[key]) * sign[key];
            if (!(span > 0)) return null;
            return { key, line: hit.line, guide: hit.guide, scale: (span / fit[key]) * 100, gap: Math.abs(hit.shift) * (key === 'x' ? aspect : 1) };
        }).filter(Boolean).sort((a, b) => (b.guide - a.guide) || (a.gap - b.gap));
        if (best) {
            scale = best.scale;
            if (best.guide) lines[best.key] = best.line;
        }
    }
    const kept = round(clamp(scale, SCALE_LIMITS.min, SCALE_LIMITS.max));
    if (Math.abs(kept - scale) > 0.01) {
        lines.x = null;
        lines.y = null;
    }
    const size = { x: (fit.x * kept) / 100, y: (fit.y * kept) / 100 };
    const leads = {
        x: sign.x > 0 ? fixed.x : fixed.x - size.x,
        y: sign.y > 0 ? fixed.y : fixed.y - size.y,
    };
    return { scale: kept, ...placeAll(frame, picture, leads, { free, scale: kept, lines }), lines };
}

/**
 * The sizes a contained picture zooms through: each puts its width or its
 * height on whole grid steps, or fits it to its page or to the sheet.
 */
export function gridScales(frame) {
    const steps = [];
    AXES.forEach(({ dim }) => {
        const fit = frame.fit[dim];
        if (!(fit > 0)) return;
        for (let count = 1; (count * SHEET_GRID * 100) / fit <= SCALE_LIMITS.max; count += 1) {
            steps.push({ value: round((count * SHEET_GRID * 100) / fit), fits: false });
        }
        steps.push({ value: round((frame.page[dim] / fit) * 100), fits: true }, { value: round((100 / fit) * 100), fits: true });
    });
    steps.push({ value: 100, fits: true });
    // Sizes within half a percent are one step, and a fit wins it.
    return steps
        .filter(({ value }) => value >= SCALE_LIMITS.min && value <= SCALE_LIMITS.max)
        .sort((a, b) => a.value - b.value)
        .reduce((kept, step) => {
            const last = kept[kept.length - 1];
            if (!last || step.value - last.value >= 0.5) kept.push(step);
            else if (step.fits && !last.fits) kept[kept.length - 1] = step;
            return kept;
        }, [])
        .map(({ value }) => value);
}

/**
 * A contained picture zoomed one step (`steps` > 0 grows it) to the next of its
 * gridScales, around `point` in sheet percent. The spot under the point stays
 * about put: the picture's edges then snap as in a move.
 *
 * @returns {{ scale: number, x: number, y: number, ox?: number, oy?: number, lines: { x: number|null, y: number|null } }|null}
 *   null when it grows or shrinks no further
 */
export function zoomPicture({ frame, picture, steps, point, free = false, snap = true }) {
    const from = picture.scale || 100;
    const scales = gridScales(frame);
    const to = steps > 0
        ? scales.find((value) => value > from + 0.01)
        : [...scales].reverse().find((value) => value < from - 0.01);
    if (to === undefined) return null;
    const lines = { x: null, y: null };
    const leads = {};
    AXES.forEach((spec) => {
        const axis = axisOf(frame, picture, spec);
        const focus = point[spec.key];
        const lead = focus - ((focus - axis.lead) * to) / from;
        const moved = snap ? snapSpan(lead, (axis.size * to) / from, frame.guides[spec.key]) : { lead, line: null };
        leads[spec.key] = moved.lead;
        lines[spec.key] = moved.line;
    });
    return { scale: to, ...placeAll(frame, picture, leads, { free, scale: to, lines }), lines };
}
